import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Film, Swords } from 'lucide-react';

import { useGetMyMatchesQuery, useGetMeQuery } from '@/services/qgammonApi';
import { endDescriptorKey, isBotMatch, matchEndDescriptor } from '@/lib/qgammon/matchOutcome';
import type { QgammonMatchSummary } from '@/types/qgammon';
import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import { cn } from '@/lib/cn';

type Outcome = 'won' | 'lost' | 'inprogress' | 'unknown';

function outcomeFor(m: QgammonMatchSummary, myId: string | null): Outcome {
  if (m.status !== 'completed' && m.status !== 'abandoned') return 'inprogress';
  if (!myId || (m.white?.id !== myId && m.black?.id !== myId)) return 'unknown';
  if (m.winner === null) return 'unknown';
  return m.winner === myId ? 'won' : 'lost';
}

/** Hub "Partite" section for Backgammon: the caller's match history + replay. */
export function QgammonMatchesPanel() {
  const { t, i18n } = useTranslation('backgammon');
  const navigate = useNavigate();
  const haptics = useHaptics();
  const { data: me } = useGetMeQuery();
  const myId = me?.user.id ?? null;
  const { data, isLoading, isError } = useGetMyMatchesQuery();
  const rows = data?.matches ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-2">
      {isLoading && (
        <div className="flex h-32 items-center justify-center">
          <Loader />
        </div>
      )}
      {isError && (
        <p className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-danger">
          {t('hubPanels.matchesError', {
            defaultValue: 'Could not load your matches. Sign in again if this persists.',
          })}
        </p>
      )}
      {!isLoading && !isError && rows.length === 0 && (
        <div className="flex flex-col items-center gap-3 rounded-xl bg-bg-secondary p-6 text-center">
          <Swords aria-hidden className="h-6 w-6 text-fg-muted" />
          <p className="text-sm text-fg-muted">
            {t('hubPanels.matchesEmpty', { defaultValue: 'No matches yet — play your first game!' })}
          </p>
        </div>
      )}
      {rows.map((m) => {
        const outcome = outcomeFor(m, myId);
        const mine = myId && (m.white?.id === myId || m.black?.id === myId);
        const oppSide = mine ? (m.white?.id === myId ? m.black : m.white) : null;
        const oppName = oppSide?.username ?? `${m.white?.username ?? '?'} · ${m.black?.username ?? '?'}`;
        const myPr = !mine ? null : m.white?.id === myId ? m.prWhite : m.prBlack;
        const finished = m.endedAt ?? m.createdAt;
        const replayable = m.status === 'completed' || m.status === 'abandoned';
        // Viewer's colour for this match, if determinable — carried via nav
        // state so the replay doesn't need to guess it from getMatch.
        const myColour: 'W' | 'B' | undefined =
          m.white?.id === myId ? 'W' : m.black?.id === myId ? 'B' : undefined;
        // Why the match ended — null when the server told us nothing we can
        // label truthfully, in which case the row looks exactly as before.
        const endedBecause = matchEndDescriptor(m);
        // Empty defaultValue + explicit guard: a missing translation must drop
        // the whole segment, not leave an orphan separator in the meta line.
        const endedLabel = endedBecause ? t(endDescriptorKey(endedBecause), { defaultValue: '' }) : '';
        const vsBot = isBotMatch(m);
        return (
          <div key={m.id} className="flex items-center gap-2 rounded-2xl bg-bg-secondary p-3">
            <button
              type="button"
              disabled={!replayable}
              onClick={() => {
                if (!replayable) return;
                haptics.selection();
                navigate(`/backgammon/replay/${m.id}`, { state: { perspective: myColour } });
              }}
              className="flex min-w-0 flex-1 items-center gap-3 text-left transition-opacity active:opacity-80 disabled:opacity-60"
            >
              <span
                className={cn(
                  'w-10 shrink-0 text-center text-xs font-bold uppercase',
                  outcome === 'won' && 'text-success',
                  outcome === 'lost' && 'text-danger',
                  (outcome === 'inprogress' || outcome === 'unknown') && 'text-fg-muted',
                )}
              >
                {t(`hubPanels.outcome.${outcome}`, {
                  defaultValue: outcome === 'won' ? 'W' : outcome === 'lost' ? 'L' : '—',
                })}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate text-sm font-semibold text-fg">{oppName}</span>
                  {vsBot && (
                    <span className="shrink-0 rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
                      {t('hubPanels.bot', { defaultValue: 'Bot' })}
                    </span>
                  )}
                </span>
                <span className="block text-xs text-fg-muted">
                  {myPr != null && (<>{t('hubPanels.pr', { defaultValue: 'PR' })} {myPr.toFixed(1)} · </>)}
                  {endedLabel !== '' && <>{endedLabel} · </>}
                  {new Date(finished).toLocaleDateString(i18n.language)}
                </span>
              </span>
            </button>
            {replayable && (
              <button
                type="button"
                aria-label={t('hubPanels.replay', { defaultValue: 'Replay' })}
                onClick={() => {
                  haptics.selection();
                  navigate(`/backgammon/replay/${m.id}`, { state: { perspective: myColour } });
                }}
                className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-border text-fg-muted transition-opacity active:opacity-70"
              >
                <Film aria-hidden className="h-4 w-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}
